import MomentUtils from '@date-io/moment';
import { Box, Button, Grid, Paper, Typography } from '@material-ui/core';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment, { Moment } from 'moment-timezone';
import React, { useState } from 'react';
import { SubmitControl } from '../../components/SubmitControl';
import useBackdrop from '../../hooks/useBackdrop';
import useSnackbar from '../../hooks/useSnackbar';
import api from '../../services/api/base';
import { IFreeStorySchedule, IFreeStoryScheduleCreator, IStory } from '../../services/types';
import StorySelectModal from '../../components/StorySelectModal';
import useStyles from './style';
import { createFreeStorySchedule } from '../../services/api/FreeStorySchedule';

interface IFreeStoryScheduleViewProps {
  onCreated?: (data: IFreeStorySchedule) => void;
  onError?: (error: Error) => void;
  onClickDeleteButton?: (scheduleId: number) => void;
}

const FreeStoryScheduleView = (props: IFreeStoryScheduleViewProps): JSX.Element => {
  const { onCreated, onError } = props;
  const classes = useStyles();

  const [selectedStory, setSelectedStory] = useState<IStory>();
  const [startAt, setStartAt] = useState<Moment>(moment());
  const [endAt, setEndAt] = useState<Moment>(moment());
  const [isStoryModalOpen, setIsStoryModalOpen] = useState<boolean>(false);

  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const stateToPayload = (): Partial<IFreeStoryScheduleCreator> => {
    return {
      storyId: selectedStory ? selectedStory.id : 0,
      startAt: startAt?.toDate(),
      endAt: endAt?.toDate(),
    };
  };

  const handleClickSubmit = async () => {
    if (selectedStory === undefined) {
      snackbar.error('Select story');
      return;
    }

    const payload: Partial<IFreeStoryScheduleCreator> = stateToPayload();

    if (moment(payload.startAt).isAfter(payload.endAt)) {
      snackbar.error(`StartAt must be after EndAt`);
      return;
    }

    try {
      backdrop.show();

      const result = await createFreeStorySchedule(payload);
      if (api.isResponseSuccess(result)) {
        setSelectedStory(undefined);
        setStartAt(moment());
        setEndAt(moment());

        if (onCreated) {
          const createdData: IFreeStorySchedule = result.data;
          createdData.story = selectedStory;
          onCreated(createdData);
        }
      }
    } catch (e) {
      console.error(e);
      if (onError) {
        onError(e);
      }
    } finally {
      backdrop.hide();
    }
    return;
  };

  const onSelectStory = (story: IStory) => {
    setSelectedStory(story);
  };

  return (
    <div className={classes.createScheduleWrap}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        style={{ width: '100%', marginTop: '10px', marginBottom: '16px', paddingLeft: '4px', paddingRight: '22px' }}
      >
        <Typography variant="h6" component="h3" style={{ flex: 1 }}>
          Create Free Story Schedule
        </Typography>
        <SubmitControl
          mode={'create'}
          onSubmitClick={() => {
            handleClickSubmit();
          }}
        />
      </Box>

      <Grid container direction="row" justify="flex-start">
        <Grid item xs={12}>
          <Paper className={classes.contentSettingWrap}>
            <Grid container direction="column" justify="flex-start" alignItems="stretch">
              <Box paddingY={2} width="100%">
                <Grid container alignItems="stretch">
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      Story
                    </Typography>
                  </Grid>
                  {selectedStory && (
                    <Grid item xs={10}>
                      [{selectedStory?.id}] {selectedStory?.title}
                    </Grid>
                  )}
                  <Grid item xs={2}>
                    <Button variant="contained" onClick={() => setIsStoryModalOpen(true)}>
                      Select
                    </Button>
                  </Grid>
                </Grid>
                <Grid container alignItems="stretch">
                  <Grid item xs={6}>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <KeyboardDateTimePicker
                        label="Event starts at"
                        format="llll"
                        required
                        margin="normal"
                        id="date-picker-inline"
                        disabled={false}
                        value={startAt}
                        minDate={moment().toDate()}
                        minDateMessage=""
                        onChange={data => {
                          if (data) setStartAt(data);
                        }}
                        className={classes.radioButtonGroups}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                  <Grid item xs={6}>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <KeyboardDateTimePicker
                        label="Event ends at"
                        format="llll"
                        required
                        margin="normal"
                        id="date-picker-inline"
                        disabled={false}
                        value={endAt}
                        onChange={data => {
                          if (data) setEndAt(data);
                        }}
                        className={classes.radioButtonGroups}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <StorySelectModal
        title="Add Stories"
        open={isStoryModalOpen}
        onClick={(story: IStory) => {
          setIsStoryModalOpen(false);
          onSelectStory(story);
        }}
        onClose={() => setIsStoryModalOpen(false)}
        filter={'story'}
      />
    </div>
  );
};

export default FreeStoryScheduleView;
